/**
 * 브랜드 테마 슬롯 스키마
 * - 메인 색 → deriveBrandSlots 자동값
 * - 사용자는 슬롯별 오버라이드 가능
 * - group: UI에서 구역별 묶음 표시
 */

/** @typedef {{
 *   id: string,
 *   label: string,
 *   where: string,
 *   example: string,
 *   group: 'shell'|'grid'|'text',
 *   themePath: string,
 *   storeAsRgba?: boolean,
 *   rgbaAlpha?: number,
 * }} AppearanceSlotDef */

/** @type {{ id: string, label: string, hint: string }[]} */
export const APPEARANCE_SLOT_GROUPS = [
  {
    id: 'shell',
    label: '① 셸·네비게이션',
    hint: '사이드 메뉴·탭·버튼·입력 포커스에 쓰이는 브랜드 색',
  },
  {
    id: 'grid',
    label: '② 그리드',
    hint: '활성 헤더·선택/hover 행·셀 선택·포커스에 쓰이는 색',
  },
  {
    id: 'text',
    label: '③ 링크·안내',
    hint: '본문 링크와 정보(info) 상태 문구',
  },
];

/** @type {AppearanceSlotDef[]} */
export const APPEARANCE_SLOT_DEFS = [
  {
    id: 'primary',
    label: '메인 액센트',
    where: '어디?',
    example: '선택 메뉴·활성 탭 밑줄·조회/저장 버튼·링크 기본색',
    group: 'shell',
    themePath: 'colors.primary',
  },
  {
    id: 'primaryDark',
    label: '메인 진한색',
    where: '어디?',
    example: '버튼·탭 hover, 조금 더 진하게 강조할 때',
    group: 'shell',
    themePath: 'colors.primaryDark',
  },
  {
    id: 'primaryLight',
    label: '메인 옅은색',
    where: '어디?',
    example: '선택 메뉴 배경, soft 배지, 연한 하이라이트',
    group: 'shell',
    themePath: 'colors.primaryLight',
  },
  {
    id: 'tabHoverActive',
    label: '컴팩트 탭 활성',
    where: '어디?',
    example: '컴팩트 호버형 탭 목록에서 현재 열린 화면 배경',
    group: 'shell',
    themePath: 'colors.tabHoverActive',
  },
  {
    id: 'focusRing',
    label: '포커스 링',
    where: '어디?',
    example: '입력칸·다이얼로그에 키보드 포커스가 갔을 때 테두리',
    group: 'shell',
    themePath: 'colors.focusRing',
  },
  {
    id: 'gridHeaderActive',
    label: '활성 그리드 헤더',
    where: '어디?',
    example: 'Alt+F2로 선택된 그리드의 컬럼 헤더 배경(파란 바)',
    group: 'grid',
    themePath: 'colors.grid.headerActiveBg',
  },
  {
    id: 'gridHeaderActiveHover',
    label: '활성 헤더 hover',
    where: '어디?',
    example: '활성 그리드 헤더 위에 마우스를 올렸을 때',
    group: 'grid',
    themePath: 'colors.grid.headerActiveHover',
  },
  {
    id: 'gridHeaderActiveFg',
    label: '활성 헤더 글자',
    where: '어디?',
    example: '활성 헤더 위 컬럼명 글자색(보통 흰색)',
    group: 'grid',
    themePath: 'colors.grid.headerActiveFg',
  },
  {
    id: 'gridHeaderActiveSort',
    label: '활성 헤더 정렬 아이콘',
    where: '어디?',
    example: '활성 헤더의 ▲▼ 정렬 표시 색(대비 자동)',
    group: 'grid',
    themePath: 'colors.grid.headerActiveSort',
  },
  {
    id: 'gridHeaderAccent',
    label: '헤더 액센트 선',
    where: '어디?',
    example: '그리드 헤더 강조 라인·구분 포인트',
    group: 'grid',
    themePath: 'colors.grid.headerAccent',
  },
  {
    id: 'gridRowSelected',
    label: '선택 행',
    where: '어디?',
    example: '클릭으로 선택된 데이터 행 배경(연한 파란 줄)',
    group: 'grid',
    themePath: 'colors.grid.rowSelected',
  },
  {
    id: 'gridRowHover',
    label: '행 hover',
    where: '어디?',
    example: '마우스를 올린 행 배경(선택보다 더 옅음)',
    group: 'grid',
    themePath: 'colors.grid.rowHover',
  },
  {
    id: 'gridCellSelected',
    label: '셀 선택(채움)',
    where: '어디?',
    example: '드래그·범위로 고른 셀 배경(반투명)',
    group: 'grid',
    themePath: 'colors.grid.cellSelected',
    storeAsRgba: true,
    rgbaAlpha: 0.28,
  },
  {
    id: 'gridCellBlockBorder',
    label: '셀 선택(테두리)',
    where: '어디?',
    example: '블럭 범위 외곽 가이드 선(선택 행과 구분)',
    group: 'grid',
    themePath: 'colors.grid.cellBlockBorder',
  },
  {
    id: 'gridCellFocused',
    label: '셀 포커스',
    where: '어디?',
    example: '현재 편집/이동 중인 셀의 포커스 테두리',
    group: 'grid',
    themePath: 'colors.grid.cellFocused',
  },
  {
    id: 'textLink',
    label: '링크',
    where: '어디?',
    example: '본문·그리드 안의 클릭 가능한 링크 글자',
    group: 'text',
    themePath: 'colors.text.link',
  },
  {
    id: 'statusInfo',
    label: '정보(info)',
    where: '어디?',
    example: '안내·정보 상태 문구(성공/오류와 별도, 브랜드 계열)',
    group: 'text',
    themePath: 'colors.status.info',
  },
];

export const DEFAULT_BRAND_MAIN = '#2563EB';

/**
 * @typedef {{
 *   mainHex: string,
 *   sourceHex?: string,
 *   softened?: boolean,
 *   colorMode?: 'light'|'dark',
 *   allowPersonalBrand?: boolean,
 *   fontFamilyId?: string,
 *   fontSizeRootPx?: number,
 *   allowPersonalFontSize?: boolean,
 *   slotOverrides?: Record<string, string>,
 *   ciMixColors?: { hex: string, weight: number }[],
 *   ciMixMode?: 'additive'|'subtractive',
 *   uiChromeType?: 'type1'|'type2',
 * }} AppearanceSettings
 */

/** @returns {AppearanceSettings} */
export function defaultAppearanceSettings() {
  return {
    mainHex: DEFAULT_BRAND_MAIN,
    sourceHex: DEFAULT_BRAND_MAIN,
    softened: false,
    colorMode: 'light',
    allowPersonalBrand: false,
    fontFamilyId: 'pretendard',
    fontSizeRootPx: 13,
    allowPersonalFontSize: true,
    slotOverrides: {},
    ciMixColors: [],
    ciMixMode: 'additive',
    uiChromeType: 'type1',
  };
}
